import React from 'react'
import { sdk } from '@/lib/sdk'
import type { LibraryTarget, MediaLibraryItem } from '@/lib/sdk-official/resources/media'

export type LibraryKind = 'all' | 'audio' | 'video'

const PAGE_SIZE = 20

/**
 * F-178 1b: one page after another of the Media library (`GET /api/media/library`), for the
 * picker and the team's screen. `reload` is outside the effect so "Tentar novamente" can call it.
 */
export const useMediaLibrary = ({ kind, q, target, enabled = true }: { kind: LibraryKind; q: string; target?: LibraryTarget; enabled?: boolean }) => {
  const [items, setItems] = React.useState<MediaLibraryItem[]>([])
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading')
  const [page, setPage] = React.useState(1)
  const [hasMore, setHasMore] = React.useState(false)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [total, setTotal] = React.useState<number | null>(null)
  const request = React.useRef(0)

  const query = React.useCallback((p: number) => ({ page: p, limit: PAGE_SIZE, ...(kind !== 'all' ? { kind } : {}), ...(q ? { q } : {}), ...(target ? { for: target } : {}) }), [kind, q, target])

  const reload = React.useCallback(async () => {
    const id = ++request.current
    setState('loading')
    try {
      const result = await sdk.media.library(query(1))
      if (id !== request.current) return
      setItems(result.items)
      setPage(1)
      setTotal(result.pagination?.total ?? result.items.length)
      setHasMore(Boolean(result.pagination && result.pagination.page < result.pagination.totalPages))
      setState('ready')
    } catch {
      if (id === request.current) setState('error')
    }
  }, [query])

  React.useEffect(() => { if (enabled) void reload() }, [enabled, reload])

  const loadMore = React.useCallback(async () => {
    if (loadingMore || !hasMore) return
    const id = request.current
    setLoadingMore(true)
    try {
      const result = await sdk.media.library(query(page + 1))
      if (id !== request.current) return
      setItems((prev) => [...prev, ...result.items.filter((item) => !prev.some((p) => p.id === item.id))])
      setPage(page + 1)
      setHasMore(Boolean(result.pagination && result.pagination.page < result.pagination.totalPages))
    } catch {
      setHasMore(false)
    } finally {
      setLoadingMore(false)
    }
  }, [hasMore, loadingMore, page, query])

  const removeLocal = React.useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id))
    setTotal((n) => (n === null ? n : Math.max(0, n - 1)))
  }, [])

  return { items, state, hasMore, loadingMore, total, reload, loadMore, removeLocal }
}
